/**
 * FarmSchedulePanel
 *
 * Every watering program on a farm, read-only: what it waters, which days, what
 * time it starts and how long the whole program takes. Editing lives on the
 * controller's Schedules tab, which is where each row links to — a farm page is
 * for seeing what is set, not for changing it.
 *
 * Start times are stored as seconds from midnight UTC while the days are local
 * (see CLAUDE.md), so the clock time shown here has to come from
 * `utcSecToLocalHHMM` and the day list has to be taken as it stands.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, Clock, PauseCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import type { OverviewController } from '../../services/irrigationOverview';
import { formatDuration, relayDisplayName, type ScheduleProgram } from '../../services/espnowIrrigation';
import { utcSecToLocalHHMM } from '../../utils/timezone';
import { startsOf } from '../../utils/programStarts';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface FarmSchedulePanelProps {
  /** Schedules are edited on the farm page's Schedules tab. */
  farmId: number;
  controllers: OverviewController[];
  programsByController: Record<string, ScheduleProgram[]>;
  loading: boolean;
  orgTimezone: string;
}

/** "Every day" / "Weekdays" / "Mon, Wed, Fri" — ISO weekdays, 1 = Monday. */
function formatDays(days: number[]): string {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  if (sorted.length === 0) return 'No days set';
  if (sorted.length === 7) return 'Every day';
  if (sorted.join(',') === '1,2,3,4,5') return 'Weekdays';
  if (sorted.join(',') === '6,7') return 'Weekends';
  return sorted.map((d) => DAY_LABELS[d - 1] ?? '?').join(', ');
}

/**
 * How long the whole program takes. A step's own duration is authoritative;
 * where it is missing, the step lasts as long as its longest relay, because the
 * relays inside a step run together rather than one after the other.
 */
function programDurationSec(program: ScheduleProgram): number {
  return (program.steps ?? []).reduce((total, step) => {
    const relayMax = (step.relays ?? []).reduce((m, r) => Math.max(m, r.duration_sec ?? 0), 0);
    return total + (step.step_duration_sec || relayMax);
  }, 0);
}

/**
 * Every time a program starts, in local clock order, each start at other than
 * full length marked with its percentage - "06:00, 14:00 (50%)".
 */
function startTimes(program: ScheduleProgram, orgTimezone: string): string[] {
  return startsOf(program)
    .map(s => ({ at: utcSecToLocalHHMM(s.start_time_sec, orgTimezone), pct: s.duration_pct }))
    .sort((a, b) => a.at.localeCompare(b.at))
    .map(s => (s.pct === 100 ? s.at : `${s.at} (${s.pct}%)`));
}

export default function FarmSchedulePanel({
  farmId,
  controllers,
  programsByController,
  loading,
  orgTimezone,
}: FarmSchedulePanelProps) {
  /** `${router_mac}-${relay_index}` → what that valve waters, for the chips. */
  const zoneLookup = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    for (const c of controllers) {
      // Relay labels first, so a drawn zone's name wins where both exist — but
      // a zone carrying only the editor's old "Relay 3" prefill does not.
      for (const r of c.routers) {
        for (const [index, label] of Object.entries(r.relay_labels ?? {})) {
          map.set(`${r.router_mac}-${index}`, { name: label, color: '#9CA3AF' });
        }
      }
      for (const z of c.zones) {
        map.set(`${z.router_mac}-${z.relay_index}`, {
          name: relayDisplayName(
            z.zone_name,
            c.routers.find((r) => r.router_mac === z.router_mac)?.relay_labels,
            z.relay_index
          ),
          color: z.zone_color,
        });
      }
    }
    return map;
  }, [controllers]);

  const groups = useMemo(
    () =>
      controllers.map((c) => ({
        controller: c,
        // Ordered by the clock time an operator reads off the card, which is the
        // local one — sorting on the stored UTC seconds puts an early morning
        // run behind every afternoon program.
        programs: [...(programsByController[c.controller_id] ?? [])].sort((a, b) =>
          startTimes(a, orgTimezone)[0].localeCompare(startTimes(b, orgTimezone)[0])
        ),
      })),
    [controllers, programsByController, orgTimezone]
  );

  const total = groups.reduce((n, g) => n + g.programs.length, 0);
  const multiController = groups.length > 1;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          <Calendar className="h-4 w-4 text-gray-400" />
          Schedules{total > 0 ? ` (${total})` : ''}
        </h2>
        {controllers.length === 1 && (
          <Link
            to={`/irrigation/farms/${farmId}?tab=schedules`}
            className="text-xs font-semibold text-blue-600 hover:text-blue-800"
          >
            Edit schedules
          </Link>
        )}
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-5 text-center text-sm text-gray-400">
          Loading schedules…
        </div>
      ) : total === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
          {controllers.length === 0
            ? 'No irrigation controllers on this farm yet.'
            : 'No watering programs have been set up yet.'}
          {controllers.length === 1 && (
            <>
              {' '}
              <Link
                to={`/irrigation/farms/${farmId}?tab=schedules`}
                className="font-semibold text-blue-600 hover:text-blue-800"
              >
                Create one
              </Link>
              .
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(({ controller, programs }) => (
            <div key={controller.controller_id}>
              {multiController && (
                <p className="mb-1.5 text-xs font-semibold text-gray-500">
                  {controller.controller_name || controller.controller_id}
                </p>
              )}
              <Card>
                <CardContent className="divide-y divide-gray-100 p-0">
                  {programs.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-500">
                      No programs on this controller.
                    </p>
                  ) : (
                    programs.map((program) => {
                      const zones = (program.steps ?? []).flatMap((s) =>
                        (s.relays ?? []).map(
                          (r) =>
                            zoneLookup.get(`${r.router_mac}-${r.relay_index}`) ?? {
                              name: `Relay ${r.relay_index}`,
                              color: '#9CA3AF',
                            }
                        )
                      );
                      const shown = zones.slice(0, 5);
                      const runtime = programDurationSec(program);
                      const stepCount = (program.steps ?? []).length;

                      return (
                        <Link
                          key={program.id}
                          to={`/irrigation/farms/${farmId}?tab=schedules&site=${encodeURIComponent(controller.controller_id)}&program=${program.id}`}
                          className="flex items-center gap-3 px-4 py-3 transition hover:bg-gray-50"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {program.name}
                              </p>
                              {!program.enabled && (
                                <span className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                                  <PauseCircle className="h-3 w-3" />
                                  Paused
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                              <span className="flex items-center gap-1 font-medium text-gray-700 tabular-nums">
                                <Clock className="h-3.5 w-3.5 text-gray-400" />
                                {startTimes(program, orgTimezone).join(', ')}
                              </span>
                              <span>{formatDays(program.days_of_week)}</span>
                              {runtime > 0 && <span>· runs {formatDuration(runtime)}</span>}
                              <span>
                                · {stepCount} step{stepCount === 1 ? '' : 's'}
                              </span>
                            </p>
                            {shown.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                                {shown.map((z, i) => (
                                  <span
                                    key={`${program.id}-zone-${i}`}
                                    className="flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600"
                                  >
                                    <span
                                      className="h-2 w-2 rounded-full"
                                      style={{ backgroundColor: z.color }}
                                    />
                                    {z.name}
                                  </span>
                                ))}
                                {zones.length > shown.length && (
                                  <span className="text-[11px] text-gray-400">
                                    +{zones.length - shown.length} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                        </Link>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
